import React, { useEffect, useState } from "react";
import api from "@/lib/api";

const STATE_STYLE = {
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
  missing: "bg-red-50 text-red-800 border-red-200",
};
const STATE_LABEL = { approved: "Uploaded · Approved", pending: "Uploaded · Pending", rejected: "Rejected", missing: "Not Uploaded" };

function fmtExp(d) {
  if (!d) return null;
  return new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function rowFor(item) {
  const files = [...(item.files || [])].sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
  const best = files.find((f) => f.status === "approved") || files[0];
  return { type: item.document_type, state: best ? (best.status || "pending") : "missing", exp: best?.expiration_date || null };
}

function PaperworkRow({ r }) {
  const expired = r.exp && new Date(`${r.exp.slice(0, 10)}T23:59:59`) < new Date();
  const slug = r.type.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  return (
    <li className="flex items-center justify-between gap-3 border border-slate-200 rounded-sm px-2 py-1.5 bg-white" data-testid={`pf-paperwork-${slug}`}>
      <span className="text-sm font-medium text-slate-900">{r.type}</span>
      <span className="flex items-center gap-2 shrink-0">
        {r.exp && <span className={`mono text-[11px] ${expired ? "text-red-700 font-bold" : "text-slate-600"}`} data-testid={`pf-paperwork-exp-${slug}`}>{expired ? "Expired" : "Exp"} {fmtExp(r.exp)}</span>}
        <span className={`text-[10px] font-bold uppercase tracking-wide border rounded-sm px-1.5 py-0.5 ${STATE_STYLE[r.state] || STATE_STYLE.pending}`} data-testid={`pf-paperwork-state-${slug}`}>{STATE_LABEL[r.state] || r.state}</span>
      </span>
    </li>
  );
}

// Required paperwork list for a member: every mandatory doc, uploaded or not.
export function ProfilePaperwork({ memberId, isSelf, refreshKey }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    const url = isSelf ? "/compliance/me" : `/compliance/user/${memberId}`;
    api.get(url).then(({ data }) => {
      const seen = new Set();
      const out = [];
      (data.sections || []).filter((s) => s.mandatory).forEach((s) => (s.items || []).forEach((it) => {
        if (it.optional || seen.has(it.document_type)) return;
        seen.add(it.document_type);
        out.push(rowFor(it));
      }));
      setRows(out);
    }).catch(() => setRows([]));
  }, [memberId, isSelf, refreshKey]);

  const done = (rows || []).filter((r) => r.state !== "missing").length;
  return (
    <section className="surface-grid mb-5" data-testid="pf-paperwork-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <div>
          <div className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">Paperwork</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Every document this member is required to upload</div>
        </div>
        {rows && <span className="mono text-[11px] text-slate-500" data-testid="pf-paperwork-count">{done}/{rows.length} uploaded</span>}
      </div>
      <div className="p-4">
        {rows === null ? (
          <div className="text-xs text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-slate-500 border border-dashed border-slate-200 rounded-sm p-3">No required paperwork.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="pf-paperwork-list">
            {rows.map((r) => <PaperworkRow key={r.type} r={r} />)}
          </ul>
        )}
      </div>
    </section>
  );
}
